import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { ViewingBannerSelf } from "../support/ViewAccess.tsx";
import { AppShell, Banner, Button, IconButton, Spinner, ToastProvider, Wordmark, useToast, type NavItem } from "@vavr/ui";
import { api, ApiError } from "../shared/api.ts";
import { OfflineBanner } from "../shared/OfflineBanner.tsx";
import { match, navigate, usePath } from "../shared/router.tsx";
import { ShellSlotsProvider, useShellSlots } from "../shared/shell-slots.tsx";
import { useLiveSocket } from "../shared/live.ts";
import { setEnabledModules, slot, useTabCounts } from "../shared/slots.tsx";
import { clearRestoreKey, useRestoreKey } from "../shared/restore.ts";
import { Handoff, tappedDoor } from "./Handoff.tsx";
import { openKv, type Kv } from "../shared/kv.ts";
import type { AppPlatform, PushBridge } from "../shared/platform.ts";
import { G, GuardianContext, type GuardianCtx } from "./context.tsx";
import { consumeMagic, GuardianSignIn, magicTokenFrom, type SessionResult } from "./SignIn.tsx";
import { registerPush } from "./Settings.tsx";
import { Home } from "./Home.tsx";
import { EventRecord, History } from "./History.tsx";
import { AddAdult, People } from "./People.tsx";
import { Delegate } from "./Delegate.tsx";
import { PickupCode } from "./PickupCode.tsx";
import { Activity } from "./Activity.tsx";
import { PassButton, askForPass } from "./PassButton.tsx";
import { useNfcPass } from "./nfcPass.ts";
import { Requests } from "./Requests.tsx";
import { Settings } from "./Settings.tsx";
import type { Overview } from "./status.ts";
import "./guardian.css";
import { UpdateRequired, useUpdateGate } from "../shared/update.tsx";

/** The screens that show the pinned "Show my pass" button: the four tabs, never a flow. */
const PASS_ON = new Set([G, `${G}/activity`, `${G}/people`, `${G}/settings`]);

const CORE_NAV: (NavItem & { href: string })[] = [
  // Four tabs (redesign 2026-09-27); the pass is the button pinned above them, and "Tell the
  // school" is a quick action on Home.
  { id: "home", label: "Home", icon: "home", href: G },
  { id: "activity", label: "Activity", icon: "list", href: `${G}/activity` },
  { id: "people", label: "People", icon: "user", href: `${G}/people` },
  { id: "settings", label: "Settings", icon: "settings", href: `${G}/settings` },
];

/** The four tabs, with modules' tabs (Messages) placed after the one they name (default Home), and their counts. */
function guardianNav(counts: Record<string, number>): (NavItem & { href: string })[] {
  const nav = [...CORE_NAV];
  for (const t of slot("guardian.tab")) {
    const at = nav.findIndex((n) => n.id === (t.after ?? "home")) + 1;
    nav.splice(at > 0 ? at : nav.length, 0, { id: t.id, label: t.label, icon: t.icon, href: `${G}/${t.path}`, count: counts[t.id] || undefined });
  }
  return nav;
}

interface Route { tab: string; title: string; back?: boolean; topBar?: () => ReactNode; el: () => ReactNode }

export function route(path: string): Route {
  let m: Record<string, string> | null;
  if ((m = match(`${G}/sites/:site/children/:child`, path))) { const p = m; return { tab: "home", title: "History", back: true, el: () => <History siteId={p.site!} childId={p.child!} /> }; }
  if ((m = match(`${G}/sites/:site/events/:id`, path))) { const p = m; return { tab: "home", title: "Record", back: true, el: () => <EventRecord siteId={p.site!} eventId={p.id!} /> }; }
  if ((m = match(`${G}/people/:site/:child/add`, path))) { const p = m; return { tab: "people", title: "Add an adult", back: true, el: () => <AddAdult siteId={p.site!} childId={p.child!} /> }; }
  if ((m = match(`${G}/handoff/:type`, path)) && (m.type === "dropoff" || m.type === "pickup")) { const k = m.type; return { tab: "home", title: k === "pickup" ? "Pick up" : "Drop off", back: true, el: () => <Handoff key={k} kind={k} /> }; }
  if (match(`${G}/people`, path)) return { tab: "people", title: "Who can collect", el: () => <People /> };
  if (match(`${G}/delegate`, path)) return { tab: "people", title: "One-time code", back: true, el: () => <Delegate /> };
  if (match(`${G}/code`, path)) return { tab: "home", title: "My pickup code", back: true, el: () => <PickupCode /> };
  if (match(`${G}/requests`, path)) return { tab: "home", title: "Tell the school", back: true, el: () => <Requests /> };
  if (match(`${G}/activity`, path)) return { tab: "activity", title: "Activity", el: () => <Activity /> };
  if (match(`${G}/settings`, path)) return { tab: "settings", title: "Settings", el: () => <Settings /> };
  // Modules' tabs (Messages), and their own pages below them (a conversation) with a back arrow.
  for (const t of slot("guardian.tab")) {
    if (match(`${G}/${t.path}`, path)) return { tab: t.id, title: t.title, topBar: t.topBar, el: t.render };
    if (path.startsWith(`${G}/${t.path}/`)) return { tab: t.id, title: t.title, back: true, topBar: t.topBar, el: t.render };
  }
  // Modules' pages (e.g. the bus's full view): their own address, so Back returns where you were.
  for (const pg of slot("guardian.page")) {
    const p = match(`${G}/${pg.path}`, path);
    if (p) return { tab: pg.tab ?? "home", title: pg.title, back: true, el: () => pg.render(p) };
  }
  return { tab: "home", title: "Today", el: () => <Home /> };
}

type Session = "loading" | "out" | "in";

/**
 * The guardian app. `client` "guardian_app" keeps a bearer token in `platform.tokens` (secure
 * storage); "web" relies on the HttpOnly cookie session.
 */
export function GuardianRoot({ platform, client, kv: kvIn }: { platform: AppPlatform; client: "guardian_app" | "web"; kv?: Kv }) {
  const path = usePath();
  const kv = useMemo(() => kvIn ?? openKv("pmu-guardian"), [kvIn]);
  const tokens = platform.tokens;
  const update = useUpdateGate({ apiBase: platform.apiBase, app: "guardian", appVersion: platform.appVersion });
  // Web: "loading" until the first overview call says whether the cookie session is valid.
  const [session, setSession] = useState<Session>(() => (tokens?.loaded ? (tokens.token ? "in" : "out") : "loading"));
  const [overview, setOverview] = useState<Overview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [consuming, setConsuming] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [n, setN] = useState(0);

  // App: follow the token store (sign-in, sign-out, a 401 that cleared it).
  useEffect(() => {
    if (!tokens) return;
    const sync = () => setSession(tokens.token ? "in" : "out");
    const off = tokens.subscribe(sync);
    if (!tokens.loaded) tokens.load().then(sync); else sync();
    return off;
  }, [tokens]);

  const onSession = useCallback(async (r: SessionResult) => {
    if (tokens && r.token && r.expires_at) await tokens.save(r.token, r.expires_at);
    setSession("in"); setN((x) => x + 1);
    navigate(G, true);
  }, [tokens]);

  // Universal link / App Link: https://<origin>/auth/magic#<token> opens the app.
  useEffect(() => platform.onDeepLink?.((url) => {
    // The live lock-screen card (worker/notify-live.ts): a tap opens home, "Show my pass" the pass.
    if (url.startsWith("vavr://live/")) {
      navigate(G);
      if (new URL(url).pathname === "/pass") askForPass();
      return;
    }
    const t = magicTokenFrom(url);
    if (!t) return;
    setConsuming(true); setLinkError(null);
    consumeMagic(t, client).then(onSession, (e: ApiError) => setLinkError(e.message)).finally(() => setConsuming(false));
  }), [platform, client, onSession]);

  // Web: the email link lands on /auth/magic, which apps/web/src/main.tsx handles.

  // Loads the overview when signed in; on the web the same call is the session probe.
  const active = session === "in" || (!tokens && session === "loading");
  useEffect(() => {
    if (!active) return;
    let live = true;
    setLoadError(null);
    api<Overview>("GET", "/api/v1/guardian/overview").then(
      (o) => { if (live) { setOverview(o); setSession("in"); } },
      (e: ApiError) => { if (!live) return; if (e.status === 401) { tokens?.clear(); setSession("out"); } else { setLoadError(e.message); setSession("in"); } },
    );
    return () => { live = false; };
  }, [active, n, tokens]);

  useEffect(() => { if (session === "in" && path === `${G}/sign-in`) navigate(G, true); }, [session, path]);

  // Push: register silently when already allowed; asking happens in context (Home, Settings).
  useEffect(() => {
    if (session !== "in" || !platform.push) return;
    platform.push.permission().then((p) => (p === "granted" ? registerPush(platform.push!, "guardian") : false)).catch(() => undefined);
    // A push may say where it leads (`link`, under the app: "messages/<conversation>").
    return platform.push.onTap?.((data) => { navigate(data?.link ? `${G}/${data.link}` : G); setN((x) => x + 1); });
  }, [session, platform]);

  // Android: the phone is also an NFC pickup pass while the app is open.
  useNfcPass(platform, kv, session === "in" ? overview : null);
  // A staff phone tapped this one and gave its door code: open the pickup to choose and sign.
  useEffect(() => {
    const nfc = platform.nfcPass;
    if (session !== "in" || !nfc) return;
    const open = (code: string) => { tappedDoor.code = code; navigate(`${G}/handoff/pickup`); };
    void nfc.takeDoor().then((c) => { if (c) open(c); });
    const wake = () => { if (document.visibilityState === "visible") void nfc.takeDoor().then((c) => { if (c) open(c); }); };
    document.addEventListener("visibilitychange", wake);
    const off = nfc.onDoor(open);
    return () => { off(); document.removeEventListener("visibilitychange", wake); };
  }, [session, platform]);

  // Live: one socket per school, scoped to this parent's children; any change refetches (PMU-202).
  const bumpTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const bump = useCallback(() => { clearTimeout(bumpTimer.current); bumpTimer.current = setTimeout(() => setN((x) => x + 1), 300); }, []);
  useEffect(() => () => clearTimeout(bumpTimer.current), []);

  const signOut = useCallback(async () => {
    await api("POST", "/api/v1/auth/logout").catch(() => undefined);
    await tokens?.clear();
    await kv.clear().catch(() => undefined);
    await platform.nfcPass?.clear().catch(() => undefined);
    await clearRestoreKey(platform);
    setOverview(null);
    setSession("out");
    navigate(client === "web" ? `${G}/sign-in` : G, true);
  }, [tokens, kv, client]);

  // Before any early return: a hook must run on every render.
  const slots = useShellSlots();
  const counts = useTabCounts(slot("guardian.tab"));
  useRestoreKey(platform, session, client, onSession);
  if (update.required) return <UpdateRequired gate={update} appVersion={platform.appVersion} />;
  if (session === "loading") return <div className="pmu-center"><Spinner label="Loading" /></div>;
  if (session === "out") {
    return <ToastProvider><GuardianSignIn client={client} passkeys={platform.passkeys} onSession={onSession} consuming={consuming} error={linkError} /></ToastProvider>;
  }

  // A module's parent screens show if any of the family's schools has it (PMU-185).
  if (overview) setEnabledModules((id) => overview.sites.some((s) => s.modules?.includes(id) ?? true));
  const r = route(path);
  const nav = guardianNav(counts);
  const ctx: GuardianCtx | null = overview ? { platform, kv, overview, reload: () => setN((x) => x + 1), version: n, signOut, client } : null;
  return (
    <ToastProvider>
      {session === "in" && platform.push?.onForeground && <ForegroundPush push={platform.push} onPush={bump} />}
      {session === "in" && overview?.sites.map((s) => <SiteLive key={s.site.id} siteId={s.site.id} apiBase={platform.apiBase} onChange={bump} />)}
      <AppShell
        brand={r.back ? <IconButton icon="chevron-left" label="Back" onClick={() => history.back()} /> : <Wordmark withMark className="pmu-g-wordmark" />}
        title={slots.title(r.title)}
        topBar={<>{ctx ? r.topBar?.() : undefined}{slots.actions}</>}
        nav={nav}
        activeId={r.tab}
        onNavigate={(id: string, e: MouseEvent) => { e.preventDefault(); navigate(nav.find((x) => x.id === id)!.href); }}
        className="pmu-guardian"
      >
        <OfflineBanner />
        {client === "web" && <ViewingBannerSelf />}
        {loadError ? <Banner tone="danger" title={loadError} actions={<Button size="sm" onClick={() => setN((x) => x + 1)}>Try again</Button>} />
          : !ctx ? <div className="pmu-center"><Spinner label="Loading" /></div>
          : <ShellSlotsProvider value={slots.value}><GuardianContext.Provider value={ctx}>
              {slot("guardian.wrap").reduceRight<ReactNode>((inner, w) => <Fragment key={w.id}>{w.wrap(inner)}</Fragment>, r.el())}
              {/* Only on the four tab screens: in a flow (scanning, signing, forms) it would cover the flow's own buttons. */}
              {ctx.overview.sites.some((s) => s.children.length) && PASS_ON.has(path.replace(/\/$/, "")) && <PassButton />}
            </GuardianContext.Provider></ShellSlotsProvider>}
      </AppShell>
    </ToastProvider>
  );
}

/** The live socket for one school; renders nothing. */
function SiteLive({ siteId, apiBase, onChange }: { siteId: string; apiBase: string; onChange: () => void }) {
  useLiveSocket({ siteId, apiBase, ticketPath: (id) => `/api/v1/guardian/sites/${id}/ws-ticket`, onChange });
  return null;
}

/** A push that arrives while the app is open: Android shows nothing, so a toast says it (and the screens refresh). */
function ForegroundPush({ push, onPush }: { push: PushBridge; onPush: () => void }) {
  const toast = useToast();
  useEffect(() => push.onForeground?.((n) => {
    // Nothing to say about the screen already open (a message in the conversation on screen).
    if (n.data?.link && location.pathname === `${G}/${n.data.link}`) { onPush(); return; }
    toast.show({ title: n.title, description: n.body, tone: "info", priority: "high", timeout: 8000 });
    onPush();
  }), [push, toast, onPush]);
  return null;
}
